import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Award } from "lucide-react";
import api, { formatMoney, formatNumber, formatPct } from "../lib/api";
import { useConsejoExcluidos } from "../lib/consejoExcluidos";
import { ExcluirConsejoBtn } from "./ExcluirConsejoBtn";

const ORDEN = [
  { k: "ratio_consejo", label: "Por ratio de consejo" },
  { k: "consejo_eur", label: "Por € de parafarmacia" },
];
const MEDAL = ["bg-amber-400 text-white", "bg-slate-400 text-white", "bg-orange-700 text-white"];

function Productos({ items: todos, excl }) {
  const items = todos.filter((p) => !excl.set.has(p.cn));
  if (!items.length) return <p className="text-xs text-slate-500 py-2">Sin parafarmacia añadida a recetas este mes.</p>;
  return (
    <table className="dense mt-1">
      <thead><tr><th>CN</th><th>Producto</th><th className="num">Recetas</th><th className="num">Uds</th><th className="num">€</th><th /></tr></thead>
      <tbody>
        {items.map((p) => (
          <tr key={p.cn}>
            <td className="font-mono text-xs">{p.cn}</td>
            <td className="text-xs">{p.descripcion}</td>
            <td className="num font-semibold text-emerald-700">{formatNumber(p.operaciones)}</td>
            <td className="num">{formatNumber(p.uds)}</td>
            <td className="num">{formatMoney(p.facturacion)}</td>
            <td><ExcluirConsejoBtn cn={p.cn} descripcion={p.descripcion} excl={excl} testid={`aconsejador-excluir-${p.cn}`} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Fila({ e, i, name, open, onToggle, max, excl }) {
  return (
    <li className="border border-slate-200 rounded-lg" data-testid={`aconsejador-${i}`}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2.5 text-left transition-colors hover:bg-slate-50" data-testid={`aconsejador-toggle-${i}`}>
        <span className={`h-7 w-7 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${MEDAL[i] || "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
        <div className="min-w-[70px] flex-1">
          <div className="text-sm font-semibold truncate">{name || e.vendedor}</div>
          <div className="h-1.5 mt-1 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${max ? (100 * e.valor) / max : 0}%` }} />
          </div>
        </div>
        <div className="text-right shrink-0 sm:w-28">
          <div className="text-sm font-bold text-emerald-700">{formatPct(e.ratio_consejo)}</div>
          <div className="text-[11px] text-slate-500">{formatNumber(e.operaciones_consejo)}/{formatNumber(e.operaciones_receta)} recetas</div>
        </div>
        <div className="text-right shrink-0 sm:w-24 font-mono text-xs sm:text-sm font-semibold">{formatMoney(e.consejo_eur)}</div>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3 pb-3 overflow-x-auto" data-testid={`aconsejador-productos-${i}`}><Productos items={e.productos} excl={excl} /></div>}
    </li>
  );
}

export const TopAconsejadores = ({ importId }) => {
  const [data, setData] = useState([]);
  const [names, setNames] = useState({});
  const [orden, setOrden] = useState("ratio_consejo");
  const [open, setOpen] = useState(null);
  const excl = useConsejoExcluidos();
  useEffect(() => {
    api.get(`/sales/imports/${importId}/aconsejadores`).then((r) => setData(r.data.empleados)).catch(() => setData([]));
    api.get("/staff").then((r) => setNames(Object.fromEntries((r.data.personal || []).map((s) => [s.vendedor, s.nombre])))).catch(() => {});
  }, [importId]);
  const rows = useMemo(() => data.map((e) => ({ ...e, valor: e[orden] })).sort((a, b) => b.valor - a.valor), [data, orden]);
  if (!data.length) return null;
  const max = rows[0]?.valor || 0;
  return (
    <div className="card p-6 mb-6" data-testid="top-aconsejadores-panel">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Award size={12} /> Equipo</div>
          <h3 className="font-display text-xl font-bold">Top aconsejadores</h3>
          <p className="text-xs text-slate-500 mt-1">Quién añade más parafarmacia (CN &lt; 400000) a sus recetas. Pulsa un empleado para ver sus productos.</p>
        </div>
        <div className="flex gap-1.5">
          {ORDEN.map((o) => (
            <button key={o.k} onClick={() => setOrden(o.k)} data-testid={`aconsejadores-orden-${o.k}`}
                    className={"px-3 py-1 rounded-full text-xs font-semibold border transition-colors " +
                      (orden === o.k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400")}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <ol className="space-y-2">
        {rows.map((e, i) => (
          <Fila key={e.vendedor} e={e} i={i} name={names[e.vendedor]} max={max}
                excl={excl} open={open === e.vendedor} onToggle={() => setOpen(open === e.vendedor ? null : e.vendedor)} />
        ))}
      </ol>
    </div>
  );
};
